import { motion } from 'framer-motion';
import { ArrowRight, Terminal } from 'lucide-react';

export default function Hero({ onGetStarted }) {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background — clean CSS gradient, no text artifacts */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-bg" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-white/[0.03] blur-[120px]" />
        <div className="absolute top-40 right-0 w-[400px] h-[400px] rounded-full bg-white/[0.02] blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="max-w-3xl"
        >
          {/* Badge */}
          <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-neutral-300 backdrop-blur-md mb-8" data-testid="hero-badge">
            <Terminal className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
            Browser-native AI development and deployment
          </div>

          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.05] text-white mb-6" data-testid="hero-title">
            Your AI agent Codelves lives<br />
            <span className="text-gradient">in your browser.</span>
          </h1>

          <p className="text-lg sm:text-xl text-neutral-400 leading-relaxed max-w-2xl mb-10 font-body" data-testid="hero-description">
            Codelves brings AI assited coding and agent creation to your device with its browser based workshop — no installs, no setup, no limits.
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={onGetStarted}
              className="group inline-flex items-center justify-center rounded-md bg-white text-black px-7 py-3.5 text-sm font-medium hover:bg-neutral-200 transition-all duration-200 focus:ring-2 focus:ring-white/50"
              data-testid="hero-cta-button"
            >
              Start building free
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
            </button>
            <button
              onClick={() => {
                const el = document.getElementById('features');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center justify-center rounded-md border border-white/10 bg-transparent px-7 py-3.5 text-sm font-medium text-white hover:bg-white/5 transition-all duration-200"
              data-testid="hero-secondary-button"
            >
              See how it works
            </button>
          </div>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl"
          data-testid="hero-stats"
        >
          {[
            { value: '100%', label: 'Browser based' },
            { value: '0', label: 'Dependencies to install' },
            { value: 'Python', label: 'Execution via Pyodide' },
            { value: 'Open', label: 'Source foundation' },
          ].map((stat) => (
            <div key={stat.label} className="border-l border-white/10 pl-4">
              <div className="font-heading text-2xl font-medium text-white">{stat.value}</div>
              <div className="text-xs text-neutral-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
